// src/components/ImageCompressor.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ImageCompressor.css';
import ImageSelector from './ImageSelector';
import { ImageComparator } from './ImageComparator';

interface ProcessingResult {
  decoded_image?: string;
  stat_file?: string;
}

interface codecStats {
  'Binary File Size'?: string;
  'Compression Ratio'?: string;
  BPP?: string;
  PSNR?: string;
  SSIM?: string;
  'Encoding Time'?: string;
}

type StatItem = {
  name: keyof codecStats;
  acuity: string;
  jpeg: string;
};

type StatsArray = StatItem[];

const styles = {
  section:
    'mb-14 flex w-full max-w-[1000px] flex-col items-center justify-center',
  title: 'mb-6 text-center text-2xl tracking-tight text-black sm:text-2xl',
  contentContainer: 'w-full max-w-[1000px]',
};

const ImageCompressor = () => {
  const [JPEGStats, setJpegStats] = useState<codecStats>({
    'Binary File Size': '',
    'Compression Ratio': '',
    BPP: '',
    PSNR: '',
    SSIM: '',
    'Encoding Time': '',
  });
  const [AcuityAIStats, setAcuityaiStats] = useState<codecStats>({
    'Binary File Size': '',
    'Compression Ratio': '',
    BPP: '',
    PSNR: '',
    SSIM: '',
    'Encoding Time': '',
  });
  const [aaiResults, setAaiResults] = useState<{
    aai: ProcessingResult;
  } | null>(null);
  const [jpgResults, setJpgResults] = useState<{
    jpg: ProcessingResult;
  } | null>(null);
  const [targetBPP, setTargetBPP] = useState('0');
  const [detInAai, setDetInAai] = useState('');
  const [detInJpg, setDetInJpg] = useState('');
  const [disableDetect, setDisableDetect] = useState(true);
  const [reconImages, setReconImages] = useState(false);
  const [processingState, setProcessingState] = useState({
    isProcessing: false,
    processingText: '',
    processingDone: false,
    showCompletionMessage: false,
  });
  const [detResults, setDetResults] = useState<{
    det: { aai: string; jpg: string };
  } | null>(null);
  const [statsForDisplay, setStatsForDisplay] = useState<StatsArray | null>(
    null
  );
  const [selectedImageUrls, setSelectedImageUrls] = useState<string[]>([]);

  const handleImagesSelected = (imageUrls: string[]) => {
    setSelectedImageUrls(imageUrls);
  };

  const createFormData = (
    file: string | Blob,
    codec = 'aai',
    action = 'compress'
  ) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('acuity-quality', '1');
    formData.append('codec', codec);
    formData.append('action', action);
    return formData;
  };

  const processFormData = async (formData: any) => {
    try {
      const backendUrl = `${process.env.REACT_APP_BACKEND_URL}/process`;
      const response = await axios.post(backendUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error processing form data:', error);
      throw error;
    }
  };

  const compressAAI = async () => {
    setProcessingState((prevState) => ({
      ...prevState,
      isProcessing: true,
      processingText: 'Compressing with AcuityAI...',
    }));

    setAaiResults(null);

    try {
      const aaiFormData = createFormData(
        selectedImageUrls[0],
        'aai',
        'compress'
      );
      const aaiProcessResponse = await processFormData(aaiFormData);
      setTargetBPP(aaiProcessResponse.bpp);
      const acuityaiFilename = aaiProcessResponse?.acuityai?.decoded_image
        .split('/')
        .pop();

      const newResults = {
        aai: {
          decoded_image: `${process.env.REACT_APP_BACKEND_URL}/image/${acuityaiFilename}`,
          stat_file: `${
            process.env.REACT_APP_BACKEND_URL
          }/image/${acuityaiFilename.replace(
            'decoded_image.png',
            'stat_file.txt'
          )}`,
        },
      };
      setDetInAai(aaiProcessResponse?.acuityai?.decoded_image);
      setAaiResults(newResults);

      setProcessingState((prevState) => ({
        ...prevState,
        isProcessing: false,
      }));
    } catch (error) {
      console.error('Error processing image:', error);
      setProcessingState((prevState) => ({
        ...prevState,
        isProcessing: false,
      }));
    }
  };

  const compressJPEG = async () => {
    setProcessingState((prevState) => ({
      ...prevState,
      processingDone: false,
      isProcessing: true,
      processingText: 'Now compressing with JPEG...',
    }));

    setJpgResults(null);

    const pth = aaiResults?.aai?.decoded_image;
    const basename = pth?.substring(pth.lastIndexOf('/') + 1);
    const jpg_file = basename?.replace('_acuityai_decoded_image', '');

    const jpgFormData = createFormData(
      selectedImageUrls[0],
      'jpeg',
      'compress'
    );
    jpgFormData.append('target-bpp', targetBPP.toString());
    jpgFormData.append('jpg_in', jpg_file ? jpg_file : '');
    const jpgProcessResponse = await processFormData(jpgFormData);

    const jpegFilename = jpgProcessResponse?.jpeg?.decoded_image
      .split('/')
      .pop();
    const newResults = {
      jpg: {
        decoded_image: `${
          process.env.REACT_APP_BACKEND_URL
        }/image/${jpegFilename.replace()}`,
        stat_file: `${
          process.env.REACT_APP_BACKEND_URL
        }/image/${jpegFilename.replace('decoded_image.png', 'stat_file.txt')}`,
      },
    };
    setDetInJpg(jpgProcessResponse?.jpeg?.decoded_image);
    setJpgResults(newResults);
    setProcessingState((prevState) => ({
      ...prevState,
      isProcessing: false,
      processingDone: true,
    }));
  };

  async function processImages() {
    try {
      if (!selectedImageUrls?.[0]) {
        console.log('Please select an example image.');
        return;
      }
      await compressAAI();
    } catch (error) {
      console.error('An error occurred during processing:', error);
    }
  }

  useEffect(() => {
    if (aaiResults) {
      compressJPEG();
      setReconImages(false);
    }
  }, [aaiResults]);

  useEffect(() => {
    if (processingState.processingDone) {
      setProcessingState((prevState) => ({
        ...prevState,
        showCompletionMessage: true,
      }));
      const timer = setTimeout(() => {
        setProcessingState((prevState) => ({
          ...prevState,
          showCompletionMessage: false,
        }));
      }, 1250);
      return () => clearTimeout(timer);
    }
  }, [processingState.processingDone]);

  const detectObjects = async () => {
    setProcessingState((prevState) => ({
      ...prevState,
      isProcessing: true,
      processingDone: false,
      processingText: 'Running object detection with YOLOv5...',
    }));

    setDetResults(null);

    const detectionFormData = createFormData('xyz', 'yolo', 'detect');
    detectionFormData.append('aai_in', detInAai);
    detectionFormData.append('jpg_in', detInJpg);

    await processFormData(detectionFormData);
    const aaifile = detInAai?.split('/').pop();
    const jpgfile = detInJpg?.split('/').pop();

    const newResults = {
      det: {
        aai: `${process.env.REACT_APP_BACKEND_URL}/image/${aaifile?.replace(
          'decoded_image.png',
          `detection_res.png`
        )}`,
        jpg: `${process.env.REACT_APP_BACKEND_URL}/image/${jpgfile?.replace(
          'decoded_image.png',
          `detection_res.png`
        )}`,
      },
    };
    setDetResults(newResults);
    setProcessingState((prevState) => ({
      ...prevState,
      isProcessing: false,
      processingDone: true,
    }));
    setDisableDetect(true);
  };

  const getStatsForDisplay = (
    acuityStats: codecStats,
    jpegStats: codecStats
  ) => {
    const keys = Object.keys(acuityStats) as Array<keyof codecStats>;
    return keys.map((key) => ({
      name: key,
      acuity: acuityStats[key] || '',
      jpeg: jpegStats[key] || '',
    }));
  };

  return (
    <div>
      {processingState.isProcessing && (
        <>
          <div id="overlay"></div>
          <div id="processing-bar">
            {processingState.processingText}
            <div className="progress-outer">
              <div className="progress-inner"></div>
            </div>
          </div>
        </>
      )}

      {processingState.showCompletionMessage && (
        <div id="processing-bar">
          <span className="message-text">Done, results available!</span>
        </div>
      )}

      <ImageSelector onImagesSelected={handleImagesSelected} />

      <div className={styles.contentContainer}>
        <div className="flex flex-col items-center justify-center">
          <div
            className="aspect-ratio-square flex flex-col items-center justify-center p-6"
            style={{ maxHeight: '48vh' }}
          >
            <img
              src={selectedImageUrls?.[0] || './data_input.jpeg'}
              alt="Selected example"
              style={{
                maxWidth: '100%',
                maxHeight: '30vh',
                objectFit: 'cover',
              }}
            />
          </div>

          <div className="mt-6">
            <button
              className="rounded bg-red-700 px-4 py-2 font-bold text-white hover:bg-red-900"
              id="process-button"
              onClick={processImages}
            >
              Compress
            </button>
          </div>

          {/* Results display */}
          <div className={styles.contentContainer}>
            <div className="mb-6 mt-6 flex w-full flex-col items-center">
              <h2 className={styles.title}>Compression Statistics</h2>
              {aaiResults && (
                <table className="stats-table min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="text-left">Statistic</th>
                      <th className="text-right">AcuityAI</th>
                      {jpgResults?.jpg?.stat_file && (
                        <th className="text-right">JPEG</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {statsForDisplay?.map((stat, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? 'bg-gray-100' : 'bg-white'}
                      >
                        <td className="text-left">
                          {stat.name === 'Binary File Size'
                            ? 'Compressed File Size'
                            : stat.name}
                        </td>
                        <td className="text-right">{stat.acuity}</td>
                        {jpgResults?.jpg?.stat_file && (
                          <td className="text-right">{stat.jpeg}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {!aaiResults && (
                <img
                  src={selectedImageUrls?.[1] || './compression_stat_table.png'}
                  alt="Compression statistics"
                  className="w-full"
                />
              )}
            </div>

            <div className="mb-6 mt-6 flex w-full flex-col items-center">
              <div className="flex min-w-full flex-col">
                <h2 className="mb-2 text-center text-2xl tracking-tight text-black sm:text-2xl">
                  Image Quality Comparison
                </h2>
                <ImageComparator
                  aai={
                    reconImages
                      ? aaiResults?.aai?.decoded_image ||
                        './acuityai_decoded_image.bmp'
                      : selectedImageUrls?.[2] || './acuityai_decoded_image.bmp'
                  }
                  jpg={
                    reconImages
                      ? jpgResults?.jpg?.decoded_image ||
                        './jpeg_decoded_image.bmp'
                      : selectedImageUrls?.[3] || './jpeg_decoded_image.bmp'
                  }
                />
              </div>

              <div className="mt-6">
                <button
                  className="rounded bg-red-700 px-4 py-2 font-bold text-white hover:bg-red-900"
                  onClick={detectObjects}
                  disabled={!(jpgResults && aaiResults) || disableDetect}
                >
                  Detect objects
                </button>
              </div>

              <div className="min-w-full">
                <h2 className={styles.title}>Object Detection Comparison</h2>
                <ImageComparator
                  aai={
                    detResults?.det?.aai
                      ? detResults.det.aai
                      : selectedImageUrls?.[4] || './acuity_detection.bmp'
                  }
                  jpg={
                    detResults?.det?.jpg
                      ? detResults.det.jpg
                      : selectedImageUrls?.[5] || './jpeg_detection.bmp'
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCompressor;
